import React, { useEffect, useState } from "react";
import { Button, Flex, Popconfirm, Space, Tooltip } from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import * as actions from "store/actions/AuthActions";
import * as userActions from "store/actions/UserActions";
import FilterBar from "components/Common/FilterBar";
import PageActions from "components/Common/PageActions";
import { useDispatch } from "react-redux";
import useUrlFilters from "hooks/useUrlFilters";

// Search and the page live in the URL so the view can be shared as a link.
const URL_FILTERS = {
  search: { type: "string" },
  page: { type: "number", default: 1 },
  pageSize: { type: "number", key: "page_size", default: 100 },
};

const ContactsTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });

  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter("search");
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  // Backend pagination state
  const currentPage = filters.page;
  const setCurrentPage = urlSetter("page");
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = urlSetter("pageSize");
  const [totalRows, setTotalRows] = useState(0);

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchContacts = async (page, size) => {
    setLoading(true);
    try {
      const response = await actions.loadContacts(page, searchTerm, size);
      if (response.status === 200) {
        const data = response.data;
        setDataSource(data.results || []);
        setTotalRows(data.count || 0);
      }
    } catch (e) {
      console.error("Error fetching contacts:", e);
    }
    setLoading(false);
  };

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    } else {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page on search
    }
  };

  // Fetch contacts when search term or pagination changes
  useEffect(() => {
    fetchContacts(currentPage, pageSize);
  }, [searchTerm, currentPage, pageSize]);

  const handleDelete = async (id) => {
    const newData = dataSource.find((item) => item.id === id);
    const response = await actions.deleteContacts(newData.id);
    console.log(response);
    if (response.status === 204) {
      // Refresh the data after deletion
      fetchContacts(currentPage, pageSize);
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        "selectedRows: ",
        selectedRows
      );
    },
  };

  const defaultColumns = [
    {
      title: "FIRST NAME",
      dataIndex: "first_name",
      key: "first_name",
      sorter: (a, b) => a?.first_name.localeCompare(b?.first_name),
      width: 220,
    },
    {
      title: "LAST NAME",
      dataIndex: "last_name",
      key: "last_name",
      sorter: (a, b) => a?.last_name.localeCompare(b?.last_name),
      width: 220,
    },
    {
      title: "EMAIL",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a?.email.localeCompare(b?.email),
      width: 220,
    },
    {
      title: "Phone",
      dataIndex: "phone_number",
      key: "phone_number",
      sorter: (a, b) => a?.phone_number.localeCompare(b?.phone_number),
      width: 220,
    },
    {
      title: "Account",
      dataIndex: "account_name",
      key: "account_name",
      sorter: (a, b) => a?.account_name.localeCompare(b?.account_name),
      width: 220,
    },
    {
      title: "Vendor",
      dataIndex: "vendor_name",
      key: "vendor_name",
      sorter: (a, b) => a?.vendor_name.localeCompare(b?.vendor_name),
      width: 220,
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      sorter: (a, b) => a?.company.localeCompare(b?.company),
      width: 220,
    },
    {
      title: "Job Title",
      dataIndex: "job_title",
      key: "job_title",
      sorter: (a, b) => a?.job_title.localeCompare(b?.job_title),
      width: 220,
    },
    {
      title: "ACTIONS",
      key: "actions",
      width: 120,
    //   fixed: "right",
      render: (_, record) => {
        return dataSource?.length >= 1 ? (
          <Space size="small">
            <Tooltip title="View">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set("viewId", record.id);
                  setSearchParams(params);
                }}
              />
            </Tooltip>
            <Tooltip title="Edit">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set("editId", record.id);
                  setSearchParams(params);
                }}
              />
            </Tooltip>
            <Popconfirm
              title="Delete this contact?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        ) : null;
      },
    },
  ];

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const [selectionType] = useState("checkbox");

  // Handle pagination change
  const handleTableChange = (page, size) => {
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    } else {
      setCurrentPage(page);
    }
  };


  return (
    <div style={{ padding: 10 }}>
      <div style={{ marginBottom: 8 }}>
        Showing {totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1}-
        {Math.min(currentPage * pageSize, totalRows)} of {totalRows}{" "}
        contacts
        </div>
      <Flex
        gap="large"
        justify="space-between"
        align="flex-start"
        wrap="wrap"
        style={{ width: "100%" }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <FilterBar
            searchValue={searchTermLive}
            onSearchChange={setSearchTermLive}
            onSearch={onSearch}
            searchPlaceholder="Search contacts"
            onRefresh={() => fetchContacts(currentPage, pageSize)}
            refreshing={loading}
          />
        </div>
        <PageActions
          primary={{
            label: "Add Contact",
            shortLabel: "Add",
            icon: <UserAddOutlined />,
            className: "add-contact-btn",
            onClick: () => {
              const params = new URLSearchParams(searchParams);
              params.set("create", "true");
              setSearchParams(params);
            },
          }}
          actions={[
            {
              key: "export",
              label: "Export Contacts",
              icon: <DownloadOutlined />,
              onClick: () => dispatch(userActions.createExportTask({ model: "Contact" })),
            },
          ]}
        />
      </Flex>
      <div style={{ marginTop: 20 }}>
        <Table
          rowSelection={{
            type: selectionType,
            ...rowSelection,
          }}
          loading={loading}
          bordered
          size="small"
          dataSource={dataSource}
          columns={columns}
          rowKey={(row) => row.id}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRows,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ["50", "100", "200"],
            onChange: handleTableChange,
          }}
          scroll={{
            x: "max-content", // Horizontal scroll
            y: "calc(100vh - 350px)", // Vertical scroll
          }}
          sticky
        />
      </div>
    </div>
  );
};
export default ContactsTable;
